import * as React from "react";
import { createPortal } from "react-dom";

import { useDialogPortalContainer } from "@/components/ui/dialog-portal-container";
import { MUI_MENU_LIST, MUI_MENU_SURFACE } from "@/components/ui/mui-field-shape";
import { pinEnterStart, pinExitStart, setGrowClock } from "@/components/ui/mui-grow";
import {
  acquireScrollLock,
  freshTypeahead,
  hideSiblings,
  makeRoomForScrollbar,
  placePaper,
  releasedOn,
  RESIZE_DEBOUNCE_MS,
  typeaheadStep,
  UNSELECTED_MOUSE_UP_DELAY_MS,
  type OpenedBy,
} from "@/components/ui/mui-select-layer";
import { cn } from "@/lib/utils";

// MUI 9.2.0's `Select multiple` with free children, on the same layer as the
// single select (`mui-menu-select.tsx`, whose header reads the shared pieces
// off MUI; the shared code is `mui-select-layer.ts`). The Editor's locations
// select is its caller (#808).

interface MuiMultiSelectProps {
  /** Controlled, as the donor's `open` / `onOpen` / `onClose` are. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What the closed field shows — `Select`'s `renderValue` output. */
  display: React.ReactNode;
  /**
   * The menu's content, free: rows that are options (`role="option"`, chosen
   * by their own click — the menu stays open), rows that are not, and any
   * other control. Every element the keyboard should walk to carries
   * `data-menu-item` and a `tabIndex`, as `MenuList` walks only the children
   * that have one.
   */
  children: React.ReactNode;
  "aria-label"?: string;
  /**
   * `SelectInput`'s `aria-labelledby`, which MUI fills from `labelId` alone. A
   * caller with no label may point it at the field's own `id`, so the
   * combobox is named by what it shows — a name MUI leaves empty.
   */
  "aria-labelledby"?: string;
  id?: string;
  disabled?: boolean;
  /** The field's own classes — the caller's input shape. */
  className?: string;
  /** Classes on the box around the field, its caret and an in-place menu. */
  anchorClassName?: string;
  caret?: (open: boolean) => React.ReactNode;
  /** MUI's default for a `Select`'s `Menu`; on unless the caller opts out. */
  scrollLock?: boolean;
  /**
   * Whether any option the menu lists is chosen — `SelectInput`'s
   * `hasSelectedItemInListRef`, which times the opening press's release (see
   * `onFieldMouseDown`).
   */
  hasSelected?: boolean;
  /**
   * `MenuProps.disablePortal`: the menu renders inside the field's own box
   * rather than in a portal — still a fixed layer over the viewport, but a
   * DOM descendant of the field's box, inheriting its font and colour.
   */
  disablePortal?: boolean;
}

/**
 * MUI's `Select multiple` with free children — the Editor's locations select
 * (`LineLocations.tsx:135-222`, #808). The same layer as `MuiMenuSelect` —
 * `Menu` → `Popover` → `Modal`: an invisible backdrop a click closes, Escape
 * closing and swallowed, Tab closing, the scroll lock, the rest of the page
 * hidden from assistive tech, the paper on the field's bottom-centre growing on
 * MUI's `Grow`, the focus back on the field at the close — with what a
 * multiple select does differently:
 *
 *   - **choosing does not close it**: a row is chosen by its own click and the
 *     menu stays up; the caller closes it through `onOpenChange`;
 *   - **its content is the caller's**: `SelectInput` clones whatever children
 *     it is handed, so the donor's menu mixes radio rows, option rows and a
 *     button. `MenuList` walks only the children carrying a `tabindex`, without
 *     wrapping (`disableListWrap`), with Home, End and its typeahead —
 *     here, the elements marked `data-menu-item`;
 *   - **it opens on the first checked option**, else on the first item it
 *     walks, else on the list until an item arrives (`MenuList`'s default
 *     active item), visibly only from the keyboard — and a click-open then
 *     ends on the paper (`InputBase`'s click, then the trap);
 *   - **it holds the focus** while open (`FocusTrap`): focus that leaves it,
 *     or falls to the body, goes back to the paper, and a Tab past either end
 *     of a child that keeps its keys loops round inside it;
 *   - **a held opening press chooses on its release** over an option, as the
 *     single select's does (`handleItemMouseUp`), timed by `hasSelected`;
 *   - **a click anywhere in it stops at its box** — the donor's `onClick` stop
 *     on the `Select`, whose in-place menu sits inside it.
 *
 * Hidden-from-assistive-tech is narrower than the donor's on purpose: MUI's
 * `ModalManager` has no mount node for an in-place `Modal`
 * (`Portal.js:57-64` never sets the ref) and so hides every child of `<body>`
 * — the app root, menu and all. Here only the menu's neighbours are hidden.
 */
export function MuiMultiSelect({
  open,
  onOpenChange,
  display,
  children,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  id,
  disabled = false,
  className,
  anchorClassName,
  caret,
  scrollLock = true,
  hasSelected = false,
  disablePortal = false,
}: MuiMultiSelectProps) {
  const portalContainer = useDialogPortalContainer();
  const listboxId = React.useId();
  const [present, setPresent] = React.useState(open);
  const [openedBy, setOpenedBy] = React.useState<OpenedBy>(null);

  const anchorRef = React.useRef<HTMLSpanElement>(null);
  const fieldRef = React.useRef<HTMLDivElement>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const paperRef = React.useRef<HTMLDivElement | null>(null);
  const placedPaper = React.useRef<HTMLDivElement | null>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const openingRelease = React.useRef<(() => void) | null>(null);
  const mouseUp = React.useRef({
    allowSelected: false,
    allowUnselected: false,
    pressedOnItem: false,
    timers: [] as ReturnType<typeof setTimeout>[],
  });
  // `MenuList`'s `hasFocusedInitialTargetRef`: set once an item takes the
  // opening focus, so a list that opened empty hands it to the first item
  // that arrives — and to none after.
  const focusedInitialItem = React.useRef(false);
  const focusedInitialList = React.useRef(false);
  // The roving tabindex's `activeItemId`: the default item from the opening,
  // then whichever item last took the focus. The arrows and the typeahead
  // start from it when the focus is elsewhere in the menu — on a radio, on a
  // name field's check.
  const activeItem = React.useRef<HTMLElement | null>(null);
  const resetMouseUpSelection = () => {
    const state = mouseUp.current;
    for (const timer of state.timers) clearTimeout(timer);
    state.timers = [];
    state.pressedOnItem = false;
    state.allowSelected = false;
    state.allowUnselected = false;
  };
  const typeahead = React.useRef(freshTypeahead());

  // The open prop is the caller's. Opening mounts the layer and closing
  // forgets what opened it, in the render that sees the change…
  if (open && !present) setPresent(true);
  if (!open && openedBy !== null) setOpenedBy(null);

  // …and what the layer does to the DOM on each edge is here.
  const wasOpen = React.useRef(open);
  // A paper `attachPaper` has just placed and timed: it grows from MUI's own
  // start, so nothing is pinned on it.
  const freshPaper = React.useRef(false);
  React.useLayoutEffect(() => {
    if (wasOpen.current === open) return;
    wasOpen.current = open;
    const paper = paperRef.current;
    if (open) {
      // A paper still growing out re-enters from the size it had reached.
      if (paper && !freshPaper.current) {
        pinEnterStart(paper);
        placePaper(paper, anchorRef.current);
        makeRoomForScrollbar(paper, listRef.current);
        setGrowClock(paper);
      }
      typeahead.current = freshTypeahead();
      focusedInitialItem.current = false;
      focusedInitialList.current = false;
      activeItem.current = null;
    } else {
      pinExitStart(paper);
      if (paper) setGrowClock(paper);
      openingRelease.current?.();
      openingRelease.current = null;
      resetMouseUpSelection();
      fieldRef.current?.focus();
    }
    freshPaper.current = false;
  }, [open]);

  const setOpen = (next: boolean, by: OpenedBy) => {
    if (next === open) return;
    if (next) setOpenedBy(by);
    onOpenChange(next);
  };
  const close = () => setOpen(false, null);

  const attachPaper = (node: HTMLDivElement | null) => {
    paperRef.current = node;
    if (!node || placedPaper.current === node) return;
    placedPaper.current = node;
    freshPaper.current = true;
    placePaper(node, anchorRef.current);
    makeRoomForScrollbar(node, listRef.current);
    setGrowClock(node);
  };

  // `MenuList`'s opening focus (`focusInitialTarget`, `MenuList.js:118-162`):
  // `getDefaultActiveItemId`'s first selected item, else the first one the
  // list walks — else the list itself, until an item arrives. Run on every
  // render while open, as MUI's re-runs whenever the default item changes:
  // a list that opened with no rooms moves to the first room as the rooms
  // appear, and never again once an item has had it.
  React.useLayoutEffect(() => {
    if (!open || !present || focusedInitialItem.current) return;
    const list = listRef.current;
    if (!list) return;
    const options = openedBy ? { focusVisible: openedBy === "keyboard" } : undefined;
    const items = menuItemsIn(list);
    const target = items.find((item) => item.getAttribute("aria-selected") === "true") ?? items[0];
    if (target) {
      focusedInitialItem.current = true;
      activeItem.current = target;
      target.focus(options);
    } else if (!focusedInitialList.current) {
      focusedInitialList.current = true;
      list.focus(options);
    }
  });

  // `FocusTrap` (`Unstable_TrapFocus/FocusTrap.js`): while open, focus that
  // leaves the layer — or falls to the page body, checked every 50ms — goes
  // back to the paper, and a Tab out past either end, through the sentinels,
  // loops to the first or last tabbable inside it.
  React.useEffect(() => {
    if (!open || !present) return;
    const contain = () => {
      // The close hands the focus back to the field before this unsubscribes.
      if (!wasOpen.current) return;
      const root = rootRef.current;
      const active = document.activeElement;
      if (!root || (active && root.contains(active) && !isSentinel(active))) return;
      // `isTopModal`: a dialog opened over the menu — the location delete's
      // confirm — holds the focus itself until it closes.
      if (dialogAbove(root)) return;
      if (active && isSentinel(active) && paperRef.current) {
        const tabbable = tabbableIn(paperRef.current);
        const target =
          active.getAttribute("data-sentinel") === "start" ? tabbable.at(-1) : tabbable[0];
        if (target) {
          target.focus();
          return;
        }
      }
      paperRef.current?.focus();
    };
    const interval = setInterval(() => {
      if (!document.activeElement || document.activeElement === document.body) contain();
    }, FOCUS_TRAP_INTERVAL_MS);
    document.addEventListener("focusin", contain);
    return () => {
      clearInterval(interval);
      document.removeEventListener("focusin", contain);
    };
  }, [open, present]);

  React.useEffect(() => {
    if (!open || !scrollLock) return;
    return acquireScrollLock();
  }, [open, scrollLock]);

  React.useEffect(() => {
    if (!open || !present) return;
    const root = rootRef.current;
    if (!root) return;
    const hidden = hideSiblings(root);
    return () => {
      for (const node of hidden) node.removeAttribute("aria-hidden");
    };
  }, [open, present]);

  React.useEffect(() => {
    if (open && paperRef.current) placePaper(paperRef.current, anchorRef.current);
  });

  // `Popover` re-places on a window scroll only at `disableScrollLock`
  // (`Popover.js:241-246`): a locked page cannot scroll while the menu is up,
  // and the paper growing out after the lock lifts stays where it was.
  React.useEffect(() => {
    if (!present || scrollLock) return;
    const onScroll = () => {
      if (paperRef.current) placePaper(paperRef.current, anchorRef.current);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [present, scrollLock]);

  React.useEffect(() => {
    if (!open) return;
    let settle: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      clearTimeout(settle);
      settle = setTimeout(() => {
        if (paperRef.current) placePaper(paperRef.current, anchorRef.current);
      }, RESIZE_DEBOUNCE_MS);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(settle);
    };
  }, [open]);

  React.useEffect(
    () => () => {
      openingRelease.current?.();
      for (const timer of mouseUp.current.timers) clearTimeout(timer);
    },
    [],
  );

  const onFieldMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    fieldRef.current?.focus();
    // `scheduleMouseUpSelection` (`SelectInput.js:355-376`): the release of
    // this press clicks the option under it once the press has been held —
    // 400ms with nothing chosen; else 200ms for an unchosen option and 400ms
    // for a chosen one.
    resetMouseUpSelection();
    const state = mouseUp.current;
    if (!hasSelected) {
      state.timers.push(
        setTimeout(() => {
          state.allowSelected = true;
          state.allowUnselected = true;
        }, UNSELECTED_MOUSE_UP_DELAY_MS * 2),
      );
    } else {
      state.timers.push(
        setTimeout(() => {
          state.allowUnselected = true;
          state.timers.push(
            setTimeout(() => {
              state.allowSelected = true;
            }, UNSELECTED_MOUSE_UP_DELAY_MS),
          );
        }, UNSELECTED_MOUSE_UP_DELAY_MS),
      );
    }
    // A release off both the field and the menu closes it.
    openingRelease.current?.();
    const onRelease = (release: MouseEvent) => {
      openingRelease.current = null;
      if (releasedOn(release, fieldRef.current) || releasedOn(release, paperRef.current)) return;
      close();
    };
    document.addEventListener("mouseup", onRelease, { capture: true, once: true });
    openingRelease.current = () => document.removeEventListener("mouseup", onRelease, true);
    setOpen(true, "pointer");
  };

  const onFieldKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ([" ", "ArrowUp", "ArrowDown", "Enter"].includes(event.key)) {
      event.preventDefault();
      setOpen(true, "keyboard");
    }
  };

  // `Menu`'s Tab close and `MenuList`'s walk, over the marked items. A key a
  // child stopped (the rename and add fields stop theirs) never arrives.
  const onListKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (!open) return;
    if (event.key === "Tab") {
      event.preventDefault();
      close();
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const items = menuItemsIn(event.currentTarget);
    const focused = document.activeElement as HTMLElement | null;
    const holding = items.findIndex((item) => item === focused || item.contains(focused));
    const at = holding !== -1 ? holding : items.indexOf(activeItem.current as HTMLElement);
    const moveTo = (index: number) => items[index]?.focus();
    if (!event.shiftKey) {
      const to =
        event.key === "ArrowDown"
          ? Math.min(at + 1, items.length - 1)
          : event.key === "ArrowUp"
            ? at === -1
              ? items.length - 1
              : Math.max(at - 1, 0)
            : event.key === "Home"
              ? 0
              : event.key === "End"
                ? items.length - 1
                : undefined;
      if (to !== undefined) {
        event.preventDefault();
        moveTo(to);
        return;
      }
    }
    if (event.key.length !== 1) return;
    const { target, handled } = typeaheadStep(
      typeahead.current,
      event.key,
      event.timeStamp,
      items,
      at,
    );
    if (target !== null) moveTo(target);
    if (handled) event.preventDefault();
  };

  const layer = present ? (
    <div
      ref={rootRef}
      data-slot="mui-menu-select-root"
      className="fixed inset-0 z-50"
      onKeyDown={(event) => {
        if (event.key !== "Escape" || event.nativeEvent.isComposing) return;
        event.stopPropagation();
        event.preventDefault();
        close();
      }}
    >
      <div
        aria-hidden="true"
        data-slot="mui-menu-select-backdrop"
        className="fixed inset-0 -z-1 bg-transparent [-webkit-tap-highlight-color:transparent]"
        onClick={(event) => {
          if (event.target === event.currentTarget) close();
        }}
      />
      <span tabIndex={0} data-sentinel="start" aria-hidden="true" />
      <div
        ref={attachPaper}
        tabIndex={-1}
        data-slot="mui-menu-select-paper"
        data-state={open ? "open" : "closed"}
        onAnimationEnd={(event) => {
          if (
            !wasOpen.current &&
            event.target === event.currentTarget &&
            event.animationName === "mui-grow-out-opacity"
          )
            setPresent(false);
        }}
        className={cn(
          MUI_MENU_SURFACE,
          "text-lennar-ink bg-white",
          "mui-menu-grow absolute min-h-4 min-w-4 overflow-x-hidden overflow-y-auto outline-0",
          "max-h-[calc(100%-96px)] max-w-[calc(100%-32px)]",
        )}
      >
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-multiselectable="true"
          tabIndex={-1}
          onKeyDown={onListKeyDown}
          // `handleItemMouseUp` on every child the select clones, the radio
          // rows too (`SelectInput.js:472-484, 610-625`): a press that began on
          // any of them is that child's click, and the next release on one
          // clears it; a release that did not, clicks the option under it
          // once the opening press was held long enough. A child marked
          // `data-select-unbound` drops the props, as a component that spreads
          // nothing does, and takes no part.
          onMouseDown={(event) => {
            if (boundItemAt(event.currentTarget, event.target))
              mouseUp.current.pressedOnItem = true;
          }}
          onMouseUp={(event) => {
            if (!boundItemAt(event.currentTarget, event.target)) return;
            const state = mouseUp.current;
            if (state.pressedOnItem) {
              state.pressedOnItem = false;
              return;
            }
            const option = optionAt(event.target);
            if (!option) return;
            const selected = option.getAttribute("aria-selected") === "true";
            if (selected ? state.allowSelected : state.allowUnselected) option.click();
          }}
          onFocus={(event) => {
            const item = (event.target as HTMLElement).closest<HTMLElement>("[data-menu-item]");
            if (item) activeItem.current = item;
          }}
          className={cn(MUI_MENU_LIST, "m-0 list-none outline-0")}
        >
          {children}
        </ul>
      </div>
      <span tabIndex={0} data-sentinel="end" aria-hidden="true" />
    </div>
  ) : null;

  return (
    <span
      ref={anchorRef}
      className={cn("relative inline-flex", anchorClassName)}
      onClick={(event) => {
        // The donor's `onClick={(event) => event.stopPropagation()}` on the
        // `Select`, whose in-place menu is inside it.
        event.stopPropagation();
        // `InputBase`'s own click (`InputBase.js:428-431`), on a click whose
        // target is the box itself: the field takes the focus — and, open,
        // the trap hands it straight to the paper. The browser aims a click
        // at the nearest box holding both its press and its release, so this
        // is every opening press released on the backdrop or the paper: a
        // click-opened menu ends on its paper, not its checked row.
        if (event.target === event.currentTarget) fieldRef.current?.focus();
      }}
    >
      <div
        ref={fieldRef}
        id={id}
        role="combobox"
        tabIndex={disabled ? undefined : 0}
        aria-controls={open ? listboxId : undefined}
        aria-disabled={disabled || undefined}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        onMouseDown={disabled ? undefined : onFieldMouseDown}
        onKeyDown={disabled ? undefined : onFieldKeyDown}
        className={className}
      >
        {display}
      </div>
      {caret?.(open)}
      {disablePortal ? layer : layer && createPortal(layer, portalContainer ?? document.body)}
    </span>
  );
}

/** `FocusTrap`'s `interval` check for focus fallen to the body. */
const FOCUS_TRAP_INTERVAL_MS = 50;

/** An open dialog over the layer: neither inside it nor holding it. */
function dialogAbove(root: HTMLElement) {
  return [...document.querySelectorAll('[role="dialog"], [role="alertdialog"]')].some(
    (dialog) => !root.contains(dialog) && !dialog.contains(root),
  );
}

function isSentinel(node: Element) {
  return node.hasAttribute("data-sentinel");
}

/**
 * `FocusTrap`'s `defaultGetTabbable`: enabled, in the tab order (radios each
 * their own stop, as the select's carry no name). Only what is drawn: a
 * control `display: none` takes no focus, so it is never the loop's target.
 */
function tabbableIn(root: HTMLElement) {
  return [
    ...root.querySelectorAll<HTMLElement>(
      'input, select, textarea, a[href], button, [tabindex], [contenteditable]:not([contenteditable="false"])',
    ),
  ].filter(
    (node) =>
      node.tabIndex >= 0 &&
      !(node as HTMLInputElement).disabled &&
      !(node instanceof HTMLInputElement && node.type === "hidden") &&
      node.getClientRects().length > 0,
  );
}

/**
 * The list's child a pointer event landed in — what `SelectInput` clones its
 * press props onto — unless it is marked `data-select-unbound`.
 */
function boundItemAt(list: HTMLElement, target: EventTarget) {
  if (!(target instanceof Node) || target === list) return null;
  let child: Node | null = target;
  while (child && child.parentNode !== list) child = child.parentNode;
  if (!(child instanceof HTMLElement) || child.hasAttribute("data-select-unbound")) return null;
  return child;
}

/** The option a pointer event landed in, if any. */
function optionAt(target: EventTarget) {
  return target instanceof Element ? target.closest<HTMLElement>('[role="option"]') : null;
}

/** The elements a multiple select's keyboard walks: marked, enabled. */
function menuItemsIn(list: HTMLElement) {
  return [
    ...list.querySelectorAll<HTMLElement>(
      "[data-menu-item]:not([aria-disabled='true']):not(:disabled)",
    ),
  ];
}
